import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Polyline, FeatureGroup, Marker, Tooltip, useMap } from 'react-leaflet';
import { getMarkerIcon } from '../MarkerIcon';
import { makeStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import { parseISO, startOfDay, endOfDay, getUnixTime, isToday, subHours } from 'date-fns';
import vehicleTrackerService from '../../../../services/VehicleTrackerService';
import { determineMarkerIcon } from '../MapVehicleItem';

const useStyles = makeStyles({
  leafletInteractive: {
    strokeWidth: 5,
    stroke: '#03a9f4'
  },
  mapComponentWrapper: {
    width: '100%'
  }
});

const ChangeView = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map]);

  return null;
};

const TrackerMapComponent = ({
  position,
  deviceId,
  vehicleTabClicked,
  dateString,
  clickedVehiclePosition,
  clickedAssetTimestamp
}) => {
  const classes = useStyles();

  const [polylineRecord, setPolylineRecord] = useState([]);
  const [routeHistory, setRouteHistory] = useState([]);
  const vehicleTrackerItem = { timestamp: clickedAssetTimestamp };

  useEffect(() => {
    const fetchRouteHistory = async () => {
      try {
        // Parse the date string
        const date = parseISO(dateString);

        // Get the start and end of the day
        let fromTimestamp = getUnixTime(startOfDay(date)); // 12:00 AM timestamp
        let toTimestamp = getUnixTime(endOfDay(date)); // 11:59 PM timestamp

        //if employee and date is today then subtract 12hours
        //if admin don't need to subtract
        // eslint-disable-next-line no-undef
        const employeeRole = localStorage.getItem('role');

        // If it's today, lag the timestamps by 12 hours
        if (isToday(new Date(dateString)) && employeeRole === 'User') {
          fromTimestamp = getUnixTime(subHours(startOfDay(date), 12)); // 12 hours lagged
          toTimestamp = getUnixTime(subHours(new Date(), 12)); // Subtract 12 hours from the current time
        }

        const vehiclePayload = {
          deviceId: deviceId,
          fromTimestamp: fromTimestamp,
          toTimestamp: toTimestamp
        };

        if (deviceId && vehicleTabClicked === 'route' && dateString) {
          const routeHistory = await vehicleTrackerService.getTelemetryRouteHistory(vehiclePayload);

          const routeRecord = routeHistory?.data?.data;
          setRouteHistory(routeRecord);

          const routePolyLine = [];
          if (routeRecord?.length > 0) {
            routeRecord.map((record) => routePolyLine.push(record.records));
          }

          setPolylineRecord(routePolyLine);
        }
      } catch (error) {
        // eslint-disable-next-line no-console, no-undef
        console.log('error is:', error);
      }
    };
    fetchRouteHistory();
  }, [dateString, deviceId, vehicleTabClicked]);

  return (
    <div style={{ display: 'flex', flex: '1' }}>
      <div className={classes.mapComponentWrapper}>
        {vehicleTabClicked === 'route' ? (
          <MapContainer center={position} zoom={11} style={{ height: '100vh', width: '100%' }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {polylineRecord.map((records, index) => {
              return (
                <FeatureGroup key={index}>
                  {routeHistory[index]?.positions?.map((position, index) => {
                    if (index === 0) {
                      return (
                        <Marker
                          key={index}
                          position={[position.latitude, position.longitude]}
                          icon={getMarkerIcon('green')}
                        >
                          <Tooltip direction="right" offset={[25, 20]} opacity={1} permanent>
                            <span>Start</span>
                          </Tooltip>
                        </Marker>
                      );
                    } else {
                      return (
                        <Marker
                          key={index}
                          position={[position.latitude, position.longitude]}
                          icon={getMarkerIcon('red')}
                        >
                          <Tooltip direction="right" offset={[25, 20]} opacity={1} permanent>
                            <span>End</span>
                          </Tooltip>
                        </Marker>
                      );
                    }
                  })}

                  <Polyline positions={records} color="blue" className={classes.leafletInteractive} />
                </FeatureGroup>
              );
            })}
          </MapContainer>
        ) : (
          <MapContainer
            center={clickedVehiclePosition}
            zoom={13}
            scrollWheelZoom={false}
            style={{ height: '100vh', width: '100%' }}
          >
            <ChangeView center={clickedVehiclePosition} zoom={13} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker
              position={clickedVehiclePosition || position}
              icon={determineMarkerIcon(vehicleTrackerItem)}
            ></Marker>
          </MapContainer>
        )}
      </div>
    </div>
  );
};

export default TrackerMapComponent;

TrackerMapComponent.propTypes = {
  position: PropTypes.any,
  deviceId: PropTypes.any,
  vehicleTabClicked: PropTypes.any,
  dateString: PropTypes.any,
  clickedVehiclePosition: PropTypes.any,
  center: PropTypes.any,
  zoom: PropTypes.any,
  clickedAssetTimestamp: PropTypes.any
};

ChangeView.propTypes = { center: PropTypes.any, zoom: PropTypes.any };
